import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { BuildingArea } from "../components/BuildingProfile/types";
import { retrofitsScaleableColumns } from "../enums/Modernization/financialDashboard";
import { isNumeric } from "./dataManipulation";

type Building = {
  gross_asset_basement_included: number;
  net_lettable_area_without_basement?: number;
};

export const getBuildingGrossArea = (building: Building | BuildingArea) => {
  return building?.gross_asset_basement_included;
};

const getBuildingNGF = (building: Building) => {
  return building?.net_lettable_area_without_basement;
};

export const getBGFToNGFRatio = (building: Building) => {
  const ngf = getBuildingNGF(building);
  if (ngf === undefined) return NaN;
  return getBuildingGrossArea(building) / ngf;
};

export const perM2ValueToNormal = ({ building, value }: { building: BuildingArea; value?: number | undefined }) => {
  const buildingGrossArea = getBuildingGrossArea(building);
  if (isNumeric(value)) return value! * buildingGrossArea;
  return value;
};

export const normalValueToPerM2 = ({ building, value }: { building: BuildingArea; value?: number | undefined }) => {
  const buildingGrossArea = getBuildingGrossArea(building);
  if (isNumeric(value)) {
    return value! / buildingGrossArea;
  }
  return value;
};

export const normalDataToPerM2 = ({
  building,
  cols,
  data,
}: {
  building: BuildingArea;
  cols: string[];
  data: any[];
}) => {
  if (!building || !data) return [];
  return data?.map((row) => {
    const scaled = { ...row };
    Object.keys(row)
      ?.filter((col) => cols?.includes(col))
      ?.forEach((col) => {
        scaled[col] = normalValueToPerM2({ building, value: scaled[col] });
      });
    return scaled;
  });
};

export const perM2DataToNormal = ({
  building,
  cols,
  data,
}: {
  building: BuildingArea;
  cols: string[];
  data: any[];
}) => {
  if (!building || !data) return [];
  return data?.map((row) => {
    const scaled = { ...row };
    Object.keys(row)
      ?.filter((col) => cols?.includes(col))
      ?.forEach((col) => {
        scaled[col] = Math.round(perM2ValueToNormal({ building, value: scaled[col] }) ?? 0);
      });
    return scaled;
  });
};

export const normalRetrofitDataToPerM2 = ({
  building,
  retrofitData,
}: {
  building: BuildingArea;
  retrofitData: any;
}) => {
  if (!retrofitData) return;
  const convertedData = normalDataToPerM2({
    building,
    cols: retrofitsScaleableColumns as unknown as string[],
    data: [retrofitData],
  });
  return convertedData[0];
};

export const perM2RetrofitDataToNormal = ({
  building,
  retrofitData,
}: {
  building: BuildingArea;
  retrofitData: any;
}) => {
  if (!retrofitData) return;
  const convertedData = perM2DataToNormal({
    building,
    cols: retrofitsScaleableColumns as unknown as string[],
    data: [retrofitData],
  });
  return convertedData[0];
};

export const convertToKilo = (value?: number) => {
  if (!isNumeric(value)) return value;
  return (value as number) / 1000;
};

const MB_IN_BYTES = 1024 * 1024;
export const convertBytesToMB = (sizeInBytes: number | undefined) => {
  if (!sizeInBytes) return undefined;
  return sizeInBytes / MB_IN_BYTES;
};

export const convertBytesToKB = (sizeInBytes: number | undefined) => {
  if (!sizeInBytes) return undefined;
  return sizeInBytes / KB_IN_BYTES;
};

const formatFileSizeInMB = (sizeInBytes: number | undefined) => {
  const sizeInMb = convertBytesToMB(sizeInBytes);
  if (!sizeInMb) return "";
  return `${sizeInMb.toFixed(1)} MB`;
};

const KB_IN_BYTES = 1024;
const formatFileSizeInKB = (sizeInBytes: number | undefined) => {
  const sizeInKb = convertBytesToKB(sizeInBytes);
  if (!sizeInKb) return "";
  return `${sizeInKb.toFixed(1)} KB`;
};

export const formatFileSize = (sizeInBytes: number | undefined) => {
  if (!sizeInBytes) return "";
  const sizeInMb = convertBytesToMB(sizeInBytes);
  if (!sizeInMb) return "";
  if (sizeInMb < 1) {
    return formatFileSizeInKB(sizeInBytes);
  }
  return formatFileSizeInMB(sizeInBytes);
};

export const formatSquareMeters = (value: number) => {
  if (!value) return "0";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
};

export const createId = () => uuidv4();

export const isInputLikeElement = (target: HTMLElement | null) =>
  target?.closest("input, textarea, select, [contenteditable=true]");

type SetSearchParamsStateOptions = { replace?: boolean };

type UseSearchParamsStateArgs<T> = {
  paramName: string;
  parse: (raw: null | string) => T;
  serialize: (value: T) => string;
};

export const useSearchParamsState = <T>({
  paramName,
  parse,
  serialize,
}: UseSearchParamsStateArgs<T>): [T, (value: T, options?: SetSearchParamsStateOptions) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(() => parse(searchParams.get(paramName)), [searchParams, paramName, parse]);

  const setValue = useCallback(
    (nextValue: T, options?: SetSearchParamsStateOptions) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, serialize(nextValue));
          return next;
        },
        { replace: options?.replace ?? false }
      );
    },
    [paramName, serialize, setSearchParams]
  );

  return [value, setValue];
};
